import { ReactNode, useCallback } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { IconBackpack, IconBus, IconYouthCard } from '@/icons';
import { LinkProps } from '@tanstack/react-router';
import type { StudentCollectionType } from '@/helpers/collectionTypes';

interface StudentNavItem {
  to: LinkProps['to'];
  icon: ReactNode;
  defaultMessage: string;
  tutorialId?: string;
}

interface StudentNavConfig {
  to: LinkProps['to'];
  icon: ReactNode;
  label: MessageDescriptor;
  tutorialId: string;
}

const STUDENT_NAV_CONFIG: Record<StudentCollectionType, StudentNavConfig> = {
  'school-student-travel': {
    to: '/$tenantId/students/travels',
    icon: <IconBus className="size-5" />,
    label: { id: 'page.students.travels.title', defaultMessage: 'Elevresor' },
    tutorialId: 'nav-students-travels',
  },
  'school-transportation': {
    to: '/$tenantId/students/transportation',
    icon: <IconBackpack className="size-5" />,
    label: {
      id: 'page.students.transportation.title',
      defaultMessage: 'Skolskjuts',
    },
    tutorialId: 'nav-students-transportation',
  },
  'school-child-and-youth': {
    to: '/$tenantId/students/child-and-youth',
    icon: <IconYouthCard className="size-5" />,
    label: {
      id: 'page.students.child-and-youth.title',
      defaultMessage: 'Barn- & ungdomskort',
    },
    tutorialId: 'nav-students-youth',
  },
};

export const useStudentNav = () => {
  const { formatMessage } = useIntl();

  return useCallback(
    (collectionTypes: StudentCollectionType[]): StudentNavItem[] => {
      return collectionTypes.map((type) => {
        const { to, icon, label, tutorialId } = STUDENT_NAV_CONFIG[type];
        return {
          to,
          icon,
          defaultMessage: formatMessage(label),
          tutorialId,
        };
      });
    },
    [formatMessage],
  );
};
